import dayjs from "dayjs";
import { ChevronDown } from "lucide-react";
import React, { useState } from "react";
import { useTranslation } from "react-i18next";

import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { cn } from "@/lib/utils";
import { useBookConsultant } from "@/networking/job.hooks";
import { ShiftTime, allocateBookableShifts, shiftTimesOverlap } from "./jobCandidates.utils";

export interface BookShiftsButtonProps {
	jobId: string;
	consultantId: string;
	consultantName: string;
	/** The still-unmanned shifts of the job — the only ones a booking may take. */
	availableShifts: ShiftTime[];
	/**
	 * Narrows the DEFAULT selection to these ids (invitation cards pass the
	 * invited shifts — the consultant was asked for exactly those, so "Boka
	 * 1 pass" must not balloon into the whole job). The picker still offers
	 * every available shift. Ids no longer available are ignored; with none
	 * left the default falls back to the full allocation.
	 */
	defaultShiftIds?: string[];
	align?: "start" | "end";
}

const segmentClasses =
	"border-0 bg-white text-success hover:bg-success-muted disabled:opacity-40 disabled:hover:bg-white";

/**
 * The direct-booking split button of the Kandidater tab. The primary segment
 * books the DEFAULT allocation in one click — every available (unmanned)
 * shift, one per overlapping time window, exactly what the "Boka N pass"
 * label counts; `defaultShiftIds` (invitation cards) narrows that default to
 * the shifts the consultant was actually asked for. The chevron segment
 * opens the advanced picker instead: the available shifts as checkboxes
 * (pre-checked with that same default, overlapping picks disabled) for
 * choosing exactly which shifts to book.
 * Both paths book INSTANTLY via POST /job/:jobId/book (no draft/save step);
 * the backend re-validates availability and cross-job conflicts.
 */
const BookShiftsButton: React.FC<BookShiftsButtonProps> = ({
	jobId,
	consultantId,
	consultantName,
	availableShifts,
	defaultShiftIds,
	align = "end",
}) => {
	const { t } = useTranslation();
	const { mutate: bookConsultant, isLoading } = useBookConsultant();

	const [open, setOpen] = useState(false);
	const [selected, setSelected] = useState<Set<string>>(new Set());

	// Preferred defaults (still available) win; they pass through the same
	// allocation so an overlapping pair among them can't both be pre-picked
	const preferred = defaultShiftIds?.length
		? availableShifts.filter((shift) => defaultShiftIds.includes(shift.id))
		: [];
	const defaultAllocation = allocateBookableShifts(preferred.length ? preferred : availableShifts);

	const shifts = [...availableShifts].sort(
		(a, b) => new Date(a.start).getTime() - new Date(b.start).getTime()
	);

	function book(shiftIds: string[], onSuccess?: () => void) {
		// Always book an explicit selection — what the button promised is
		// exactly what gets booked, even if the job changed server-side (a
		// taken shift then errors instead of silently rebalancing). Errors
		// (e.g. a conflict on another job) are toasted by the hook.
		bookConsultant({ jobId, consultantId, shiftIds }, { onSuccess });
	}

	function handleOpenChange(nextOpen: boolean) {
		// Every opening starts from the default allocation — same set the
		// primary segment books
		if (nextOpen) setSelected(new Set(defaultAllocation.map((s) => s.id)));
		setOpen(nextOpen);
	}

	function toggle(shift: ShiftTime) {
		setSelected((prev) => {
			const next = new Set(prev);
			if (next.has(shift.id)) next.delete(shift.id);
			else next.add(shift.id);
			return next;
		});
	}

	return (
		<Popover open={open} onOpenChange={handleOpenChange}>
			<span className="inline-flex items-stretch overflow-hidden rounded border border-solid border-success">
				{/* Primary: one-click booking of the default allocation */}
				<button
					type="button"
					disabled={isLoading}
					onClick={() => book(defaultAllocation.map((s) => s.id))}
					className={cn(segmentClasses, "px-3 py-1.5 type-label font-semibold")}
				>
					{isLoading ? "..." : t("jobCandidates.bookNShifts", { count: defaultAllocation.length })}
				</button>
				{/* Chevron: pick exactly which shifts to book */}
				<PopoverTrigger asChild>
					<button
						type="button"
						disabled={isLoading}
						aria-label={t("jobCandidates.chooseShifts") as string}
						title={t("jobCandidates.chooseShifts") as string}
						className={cn(segmentClasses, "border-l border-solid border-success/40 px-1.5")}
					>
						<ChevronDown className="h-3.5 w-3.5" />
					</button>
				</PopoverTrigger>
			</span>

			<PopoverContent align={align} sideOffset={6} className="w-80 p-0">
				<div className="border-b border-border px-4 py-3">
					<div className="type-body font-semibold">
						{t("jobCandidates.bookShiftsTitle", { name: consultantName })}
					</div>
					<p className="m-0 mt-0.5 type-meta text-muted-foreground">
						{t("jobCandidates.bookShiftsHint")}
					</p>
				</div>

				<div className="max-h-72 overflow-y-auto py-1">
					{shifts.map((shift) => {
						const isChecked = selected.has(shift.id);
						// A shift overlapping an already-checked one can't be added —
						// one consultant can't work parallel shifts
						const isBlocked =
							!isChecked &&
							shifts.some((other) => selected.has(other.id) && shiftTimesOverlap(other, shift));
						return (
							<label
								key={shift.id}
								title={isBlocked ? (t("jobCandidates.overlapsSelected") as string) : undefined}
								className={cn(
									"flex items-center gap-2.5 px-4 py-1.5 type-body",
									isBlocked ? "cursor-not-allowed opacity-40" : "cursor-pointer hover:bg-gray-100"
								)}
							>
								<input
									type="checkbox"
									checked={isChecked}
									disabled={isBlocked || isLoading}
									onChange={() => toggle(shift)}
								/>
								<span className="capitalize tabular-nums text-gray-900">
									{dayjs(shift.start).locale("sv").format("ddd D/M")}
								</span>
								<span className="tabular-nums text-muted-foreground">
									{dayjs(shift.start).format("HH:mm")}–{dayjs(shift.end).format("HH:mm")}
								</span>
							</label>
						);
					})}
				</div>

				<div className="flex items-center justify-end border-t border-border px-4 py-2.5">
					<button
						type="button"
						disabled={selected.size === 0 || isLoading}
						onClick={() => book([...selected], () => setOpen(false))}
						className="rounded border-0 bg-success px-3 py-1.5 type-label font-semibold text-white hover:opacity-90 disabled:opacity-40"
					>
						{isLoading ? "..." : t("jobCandidates.bookNShifts", { count: selected.size })}
					</button>
				</div>
			</PopoverContent>
		</Popover>
	);
};

export default BookShiftsButton;
